import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import { makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { AEROS } from "../../styles/aerosTokens";

import SchedulesTab from "../../components/Settings/SchedulesTab";
import HelpsManager from "../../components/HelpsManager";
import SmsPacotesManager from "../../components/SmsPacotesManager";
import SmsRotasManager from "../../components/SmsRotasManager";
import LeadsAdminPanel from "../../components/LeadsAdminPanel";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import OnboardingAssistantSettings from "../../components/OnboardingAssistantSettings";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";

// Fiel ao mockup "Configurações A" (docs/design/aeros-dark/mockups/Telas Aeros
// Dark - Kanban, Campanhas e Configurações.dc.html): header 60px com o
// título, barra de abas com sublinhado em gradiente e conteúdo direto no
// fundo (sem Paper) — os cards de cada aba já trazem a própria superfície.
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      padding: "0 12px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
    },
    pageBody: {
      flex: 1,
      padding: "16px 12px 28px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    tabsBar: {
      display: "flex",
      gap: 2,
      flexShrink: 0,
      overflowX: "auto",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      marginBottom: 18,
    },
    tab: {
      appearance: "none",
      background: "none",
      border: "none",
      borderBottom: "2px solid transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      padding: "8px 14px",
      fontSize: 12.5,
      fontWeight: 500,
      whiteSpace: "nowrap",
      color: theme.palette.text.secondary,
      "&:hover": { color: theme.palette.text.primary },
    },
    tabActive: {
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: "2px solid",
      borderImage: "linear-gradient(90deg,#A044FF,#22D3EE) 1",
    },
    container: {
      width: "100%",
    },
  };
});

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
  }, []);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const handleSaveOutOfHoursMessage = async (value) => {
    try {
      await api.put("/companySettings", { column: "outOfHoursMessage", data: value });
      setSettings((prev) => ({ ...prev, outOfHoursMessage: value }));
      toast.success("Mensagem fora do expediente salva.");
    } catch (e) {
      toast.error("Não foi possível salvar a mensagem.");
    }
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  const tabs = [
    { value: "options", label: i18n.t("settings.tabs.options") },
    schedulesEnabled && { value: "schedules", label: "Horários" },
    user.profile === "admin" && {
      value: "finalizacao",
      label: "Finalização",
    },
    // Empresas e Planos saíram daqui em 09/2026: viraram as telas /companies
    // e /plans, no grupo "Plataforma" da sidebar.
    isSuper() && { value: "helps", label: i18n.t("settings.tabs.helps") },
    isSuper() && { value: "whitelabel", label: "Whitelabel" },
    isSuper() && { value: "smsPacks", label: i18n.t("settings.tabs.smsPacks") },
    isSuper() && { value: "leads", label: i18n.t("aeros.leadsAdmin.tab") },
  ].filter(Boolean);

  return (
    <MainContainer>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.pageHeader}>
            <Typography component="div" className={classes.pageTitle}>
              {i18n.t("settings.title")}
            </Typography>
          </div>
          <div className={classes.pageBody}>
            <nav className={classes.tabsBar} aria-label={i18n.t("settings.title")}>
              {tabs.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={`${classes.tab} ${tab === t.value ? classes.tabActive : ""}`}
                  aria-current={tab === t.value ? "page" : undefined}
                  onClick={() => handleTabChange(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            {/* Renderização condicional simples - apenas a aba ativa renderiza */}
            {tab === "schedules" && (
              <div className={classes.container}>
                <SchedulesTab
                  loading={loading}
                  onSubmit={handleSubmitSchedules}
                  initialValues={schedules}
                  outOfHoursMessage={settings.outOfHoursMessage || ""}
                  onOutOfHoursMessageSave={handleSaveOutOfHoursMessage}
                />
              </div>
            )}

            {tab === "smsPacks" && isSuper() && (
              <div className={classes.container}>
                <SmsPacotesManager />
                <SmsRotasManager />
              </div>
            )}

            {tab === "leads" && isSuper() && (
              <div className={classes.container}>
                <LeadsAdminPanel />
              </div>
            )}

            {tab === "helps" && isSuper() && (
              <div className={classes.container}>
                <HelpsManager />
              </div>
            )}

            {tab === "whitelabel" && isSuper() && (
              <div className={classes.container}>
                <Whitelabel settings={oldSettings} />
              </div>
            )}

            {tab === "finalizacao" && (
              <div className={classes.container}>
                <FinalizacaoAtendimento
                  settings={settings}
                  onSettingsChange={(newSettings) => setSettings(newSettings)}
                />
              </div>
            )}

            {tab === "options" && (
              <div className={classes.container}>
                <Options
                  settings={settings}
                  oldSettings={oldSettings}
                  user={currentUser}
                  scheduleTypeChanged={(value) =>
                    setSchedulesEnabled(value === "company")
                  }
                />
                <OnboardingAssistantSettings />
              </div>
            )}
          </div>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;